import React from "react";
import type { ReactNode } from "react";
import { AuthAdapter } from "../../api/auth/AuthAdapter";
import { AppConfigContext } from "../app/appConfig";
import { AuthModel } from "../../api/auth/AuthModel";
import type { PortalCustomer, PortalToken } from "../../api/auth/Auth.typed";
import Cookies from "js-cookie";
import { type JwtPayload, jwtDecode } from "jwt-decode";
import { useAlert } from "../app/notificationToast";
import { useNavigate } from "react-router-dom";
import { useLoginRedirect } from "../app/useLoginRedirect";

interface AuthProviderProps {
  children: ReactNode;
}

export interface AuthContextType {
  token: string | null;
  loginPortal: (customer: PortalCustomer, companyId?: string) => Promise<PortalToken | undefined>;
  logout: () => void;
  clearSession: () => void;
  checkTokenExpiration: () => void;
}

export const AuthContext = React.createContext<AuthContextType>(null!);

export function AuthProvider({ children }: AuthProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const navigate = useNavigate();
  const { buildLoginUrl } = useLoginRedirect();
  const { showAlert } = useAlert();
  const [token, setToken] = React.useState<string | null>(Cookies.get("authToken") || null);

  const authModel = React.useMemo(() => new AuthModel(appConfig), [appConfig]);
  const authClient = React.useMemo(() => new AuthAdapter(authModel), [authModel]);

  const loginPortal = React.useCallback(
    async (customer: PortalCustomer, companyId?: string): Promise<PortalToken | undefined> => {
      try {
        const response = await authClient.loginPortal(customer, companyId);
        if (response) {
          setToken(response.Token);
          Cookies.set("authToken", response.Token, { expires: 1 });
        }
        return response;
      } catch (error) {
        if (error instanceof Error) {
          showAlert(error.message, "danger");
        }
        return undefined;
      }
    },
    [authClient, showAlert],
  );

  const logout = React.useCallback(() => {
    const loginUrl = buildLoginUrl(token);
    setToken(null);
    Cookies.remove("authToken");
    navigate(loginUrl);
  }, [buildLoginUrl, navigate, token]);

  const clearSession = React.useCallback(() => {
    setToken(null);
    Cookies.remove("authToken");
  }, []);

  const checkTokenExpiration = React.useCallback(() => {
    if (!token) return;
    try {
      const decodedToken = jwtDecode<JwtPayload>(token);
      if (decodedToken.exp) {
        const expDate = new Date(decodedToken.exp * 1000);
        if (expDate < new Date()) {
          showAlert("Session expirée !", "warning");
          logout();
        }
      }
    } catch (error) {
      console.error("Erreur lors du décodage du token", error);
    }
  }, [logout, showAlert, token]);

  React.useEffect(() => {
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkTokenExpiration]);

  const value = React.useMemo<AuthContextType>(
    () => ({
      token,
      loginPortal,
      logout,
      clearSession,
      checkTokenExpiration,
    }),
    [token, loginPortal, logout, clearSession, checkTokenExpiration],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
